import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Tag, Users, X } from "lucide-react";
import api from "@/lib/api";
import { studioApi, errMessage } from "@/lib/studioApi";

const key = (s) => (s || "").trim().toLowerCase();

/**
 * "In the room" — one chip per person who may end up on camera (event lineup + saved plate
 * library + anyone added during the show). Click = flash their name plate, shift-click = keep
 * it on. Meant for room cameras (Owl) where nobody is registered behind the shot, but works
 * whatever is on air.
 */
export default function InTheRoom({ show, program, patchProgram, disabled }) {
  const [plates, setPlates] = useState(null);
  const [lineup, setLineup] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", title: "", org: "" });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const loadPlates = useCallback(() => studioApi.listPlates().then(setPlates).catch(() => {}), []);
  useEffect(() => { loadPlates(); }, [loadPlates]);
  useEffect(() => {
    if (!show?.event_id) { setLineup([]); return; }
    api.get(`/events/${show.event_id}`).then((r) => setLineup(r.data?.people || [])).catch(() => setLineup([]));
  }, [show?.event_id]);

  const people = useMemo(() => {
    const seen = new Set();
    const out = [];
    lineup.forEach((p) => {
      if (!p?.name || seen.has(key(p.name))) return;
      seen.add(key(p.name));
      out.push({ id: `lineup-${p.id || key(p.name)}`, name: p.name, title: p.title || p.role || "", org: p.org || "", lineup: true });
    });
    (plates || []).forEach((p) => {
      if (seen.has(key(p.name))) return;
      seen.add(key(p.name));
      out.push({ id: p.plate_id, name: p.name, title: p.title || "", org: p.org || "", plate_id: p.plate_id });
    });
    return out;
  }, [lineup, plates]);

  const shown = filter ? people.filter((p) => `${p.name} ${p.title} ${p.org}`.toLowerCase().includes(filter.toLowerCase())) : people;
  const lt = program?.lower_third || {};
  const activeKey = lt.visible && !lt.participant_id ? key(lt.name) : null;
  const pinnedKey = activeKey && !lt.auto ? activeKey : null;

  const tag = (p, pin) => {
    // Pinned -> hide. Auto-flashing -> flash again (restart the clock) or pin with shift.
    if (pinnedKey && pinnedKey === key(p.name)) return patchProgram({ lower_third: { visible: false } });
    const body = { participant_id: "", name: p.name, title: p.title, org: p.org };
    return patchProgram({ lower_third: pin ? { ...body, visible: true } : { ...body, flash: true } });
  };

  const add = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    setSaving(true);
    try {
      const saved = await studioApi.savePlate({ name: draft.name.trim(), title: draft.title.trim() || null, org: draft.org.trim() || null });
      await loadPlates();
      setDraft({ name: "", title: "", org: "" });
      setAdding(false);
      toast.success(`${saved.name} added to the room`);
    } catch (err) { toast.error(errMessage(err)); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!p.plate_id || !window.confirm(`Remove ${p.name} from the saved plates?`)) return;
    try { await studioApi.deletePlate(p.plate_id); loadPlates(); } catch (err) { toast.error(errMessage(err)); }
  };

  return (
    <section className="rounded-xl border border-white/10 bg-[#0f1520]" data-testid="in-the-room">
      <header className="flex flex-wrap items-center gap-3 border-b border-white/10 px-4 py-2.5">
        <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400"><Users className="h-3.5 w-3.5 text-[#e6a25f]" /> In the room</h3>
        <span className="text-[11px] text-zinc-500">Click a name to flash their plate · shift-click keeps it on</span>
        <div className="ml-auto flex items-center gap-2">
          {people.length > 8 && <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find…" className="w-28 rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1 text-xs" data-testid="room-filter" />}
          <button type="button" onClick={() => setAdding((v) => !v)} disabled={disabled} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-zinc-200 hover:bg-white/10 disabled:opacity-50" data-testid="room-add-toggle">
            {adding ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {adding ? "Cancel" : "Add person"}
          </button>
        </div>
      </header>
      <div className="p-3">
        {adding && (
          <form onSubmit={add} className="mb-3 grid gap-2 sm:grid-cols-[1.2fr_1fr_1fr_auto]" data-testid="room-add-form">
            <input autoFocus value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name" required className="rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="room-add-name" />
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Title (optional)" className="rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="room-add-title" />
            <input value={draft.org} onChange={(e) => setDraft({ ...draft, org: e.target.value })} placeholder="Organization (optional)" className="rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="room-add-org" />
            <button type="submit" disabled={saving || !draft.name.trim()} className="inline-flex items-center justify-center gap-1 rounded-md bg-[#c9772e] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#d98a3f] disabled:opacity-50" data-testid="room-add-submit">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Save
            </button>
          </form>
        )}
        {plates === null ? (
          <p className="text-xs text-zinc-600" data-testid="room-loading">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="text-xs text-zinc-500" data-testid="room-empty">{people.length === 0 ? "Nobody here yet. Link an event with a lineup, or add the people in the room so you can tag them when the camera cuts to them." : "No match."}</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="room-chips">
            {shown.map((p) => {
              const on = activeKey && activeKey === key(p.name);
              return (
                <div key={p.id} className={`group relative flex items-center rounded-full border transition ${on ? "border-[#c9772e] bg-[#c9772e]/20 text-[#e6a25f] shadow-[0_0_0_3px_rgba(201,119,46,0.15)]" : "border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"}`} data-testid={`room-chip-${p.id}`} data-on={on ? "1" : "0"}>
                  <button type="button" disabled={disabled} onClick={(e) => tag(p, e.shiftKey)} title={on ? "Hide name plate" : `Flash ${p.name}'s name plate (shift-click keeps it on)`} className="flex items-center gap-1.5 py-1 pl-3 pr-2 text-left disabled:opacity-50" data-testid={`room-tag-${p.id}`}>
                    <Tag className={`h-3 w-3 ${on ? "text-[#e6a25f]" : "text-zinc-500"}`} />
                    <span className="text-xs font-medium">{p.name}</span>
                    {(p.title || p.org) && <span className="hidden text-[11px] text-zinc-500 sm:inline">· {[p.title, p.org].filter(Boolean).join(", ")}</span>}
                    {p.lineup && <span className="rounded bg-white/10 px-1 text-[9px] uppercase tracking-wider text-zinc-400">lineup</span>}
                  </button>
                  {p.plate_id && !disabled && (
                    <button type="button" onClick={() => remove(p)} title="Remove from saved plates" className="mr-1 hidden rounded-full p-0.5 text-zinc-500 hover:bg-white/10 hover:text-white group-hover:inline-flex" data-testid={`room-remove-${p.id}`}><X className="h-3 w-3" /></button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
